import { IdToken } from '@auth0/auth0-react';
import auth0 from 'auth0-js';
import { WAS_BACK_END_API_URL, URL_VERSION } from 'src/constants';
import { HttpResponse, fetchAuthenticated } from 'src/controllers';
import { ApproverAjaxResult, ExternalUserVerifyContextResult } from 'src/shared/types/types';
import { useAuthentication } from './primaryAuth';

export interface AuthenticationProps {
  checkAuthorization: () => Promise<ExternalUserVerifyContextResult>;
  secondLogin: (result: IdToken) => Promise<void>;
}

export function SecondAuth() {
  const { webAuth, webAuthConfig, getDbUserObjectId, getTokenSilently } = useAuthentication();

  const verifyApprover = async (): Promise<HttpResponse<ApproverAjaxResult>> => {
    // This calls a custom backend API endpoint to validate the user token and return their user info
    // to check their role without messing with session data.
    return new Promise<HttpResponse<ApproverAjaxResult>>(async (resolve, reject) => {
      const tokenIDClaim = await getTokenSilently();
      const sessionDocId = getDbUserObjectId();
      const url = `${WAS_BACK_END_API_URL}${URL_VERSION}/verifyapprover/${sessionDocId}`;
      const config: RequestInit = {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${tokenIDClaim?.__raw}`,
        },
      };
      const response = await fetchAuthenticated<ApproverAjaxResult>(url, config);
      if (response?.ok && response?.parsedBody) {
        return resolve(response);
      } else {
        return reject(response);
      }
    });
  };

  const authorizePopup = async (webAuthConfig: auth0.AuthOptions): Promise<ExternalUserVerifyContextResult> => {
    const obj = await getDbUserObjectId();
    return new Promise<ExternalUserVerifyContextResult>((resolve, reject) => {
      webAuth.popup.authorize(
        {
          redirectUri: `${window.origin}/handleauth?${obj}`,
          responseType: `${webAuthConfig.responseType}`,
          domain: webAuthConfig.domain,
        },
        async () => {
          setTimeout(() => {
            try {
              verifyApprover()
                .then((result) => {
                  if (result?.parsedBody?.result?.approverStatus) {
                    return resolve({
                      verified: true,
                      errors: [],
                    });
                  } else {
                    throw new Error('Error: Approver Status False');
                  }
                })
                .catch((error) => {
                  return reject({
                    verified: false,
                    errors: [error],
                  });
                });
            } catch (error) {
              return reject({
                verified: false,
                errors: [error],
              });
            }
          }, 3000);
        }
      );
    });
  };

  const secondLogin = async (result: IdToken, docId: string) => {
    const userSub = result?.idTokenPayload?.sub;
    const tokenIDClaim = await getTokenSilently();

    const url = `${WAS_BACK_END_API_URL}${URL_VERSION}/approverauthentication`;

    const body = JSON.stringify({
      userSub: userSub,
      docId: docId,
    });

    const config: RequestInit = {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${tokenIDClaim?.__raw}`,
      },
      body,
    };
    await fetchAuthenticated<ApproverAjaxResult>(url, config);
  };

  return {
    checkAuthorization: () => authorizePopup(webAuthConfig),
    secondLogin: (result: IdToken, docId: string) => secondLogin(result, docId),
  };
}
